// On-screen play surface (touch/mouse). Renders pads for the active
// instrument: drum voices, or a row of scale-degree pads labelled with the
// note they currently play. Taps flow through ctx.play().

import type { InstrumentDef } from "../instruments/registry.ts";
import { degreeToNote, pitchClass } from "../util/scales.ts";
import type { ScaleName } from "../util/types.ts";
import type { PlayContext } from "./keyboard.ts";

const DRUM_PADS: ReadonlyArray<{ voice: string; label: string; hint: string }> = [
  { voice: "kick", label: "Kick", hint: "Z" },
  { voice: "snare", label: "Snare", hint: "X" },
  { voice: "hat", label: "Hat", hint: "C" },
  { voice: "tom", label: "Tom", hint: "V" },
  { voice: "clap", label: "Clap", hint: "B" },
  { voice: "crash", label: "Crash", hint: "N" },
];

const MELODIC_DEGREES = [0, 1, 2, 3, 4, 5, 6, 7];
const MELODIC_HINTS = ["A", "S", "D", "F", "G", "H", "J", "K"];
const PAD_VELOCITY = 0.85;
const LABEL_OCTAVE = 4; // only used to derive the pitch class label

export class TouchInput {
  readonly element: HTMLElement;
  private def: InstrumentDef;
  private key = "C";
  private scale: ScaleName = "majorPentatonic";

  constructor(
    private readonly ctx: PlayContext,
    def: InstrumentDef,
  ) {
    this.def = def;
    this.element = document.createElement("div");
    this.element.className = "pads";
    this.render();
  }

  setInstrument(def: InstrumentDef): void {
    this.def = def;
    this.render();
  }

  setScale(key: string, scale: ScaleName): void {
    this.key = key;
    this.scale = scale;
    if (this.def.scaleLocked) this.render();
  }

  private render(): void {
    this.element.replaceChildren();
    this.element.dataset["kind"] = this.def.scaleLocked ? "melodic" : "drums";

    if (this.def.scaleLocked) {
      MELODIC_DEGREES.forEach((degree, i) => {
        const label = pitchClass(degreeToNote(degree, this.key, this.scale, LABEL_OCTAVE));
        const btn = this.makePad(label, MELODIC_HINTS[i] ?? "");
        btn.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          this.ctx.play({ degree, velocity: PAD_VELOCITY, time: 0 });
          flash(btn);
        });
        this.element.append(btn);
      });
    } else {
      for (const pad of DRUM_PADS) {
        const btn = this.makePad(pad.label, pad.hint);
        btn.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          this.ctx.play({ note: pad.voice, velocity: PAD_VELOCITY, time: 0 });
          flash(btn);
        });
        this.element.append(btn);
      }
    }
  }

  private makePad(label: string, hint: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pad-btn";
    const main = document.createElement("span");
    main.className = "pad-btn__label";
    main.textContent = label;
    const sub = document.createElement("span");
    sub.className = "pad-btn__hint";
    sub.textContent = hint;
    btn.append(main, sub);
    return btn;
  }
}

function flash(btn: HTMLElement): void {
  btn.classList.remove("is-flash");
  void btn.offsetWidth;
  btn.classList.add("is-flash");
}
